'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Text, Picture, Button, Container, Heading } from '../../components/index.js'
import { cva } from 'class-variance-authority'
import type { GiftShelfProps, GiftShelfItem, CountdownConfig, ResponsiveBackgroundImage } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { withLayout } from '../../shared/Styles.js'
import type { Product, ProductVariant } from '../Listing/types/product.js'
import { formatVariantPrice } from '../Listing/utils/index.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { replaceTemplate } from '../Listing/utils/textFormat.js'
import type { GiftShelfProduct } from './types.js'
import type { Media } from '../../types/props.js'

/**
 * Card style variants for gift shelf
 */
const giftCardVariants = cva('desktop:px-6 rounded-card relative min-w-0 overflow-hidden p-4', {
  variants: {
    state: {
      light: 'bg-[#EAEAEC]',
      dark: 'bg-[#1E2024]',
    },
  },
  defaultVariants: {
    state: 'light',
  },
})

/**
 * Content layout variants - 控制 Background image 和 Bottom info area 的布局
 * horizontal 布局仅在 desktop (≥1440px) 断点生效
 */
const contentLayoutVariants = cva('', {
  variants: {
    layout: {
      vertical: '',
      horizontal: 'desktop:flex desktop:flex-row desktop:items-stretch desktop:gap-10',
    },
  },
  defaultVariants: {
    layout: 'vertical',
  },
})

/**
 * Image area variants
 * horizontal 布局仅在 desktop (≥1440px) 断点生效
 */
const imageAreaVariants = cva(
  'desktop:max-w-[268px] lg-desktop:max-w-[356px] lg-desktop:h-[171px] relative mx-auto h-[118px] max-w-[256px] ',
  {
    variants: {
      layout: {
        vertical: '',
        horizontal: 'desktop:flex-1 desktop:h-[122px] lg-desktop:h-[161px]',
      },
    },
    defaultVariants: {
      layout: 'vertical',
    },
  }
)

/**
 * Info area variants
 * horizontal 布局仅在 desktop (≥1440px) 断点生效
 */
const infoAreaVariants = cva('mt-3', {
  variants: {
    layout: {
      vertical: '',
      horizontal: 'desktop:mt-0 desktop:flex-1 desktop:flex desktop:flex-col desktop:justify-center',
    },
  },
  defaultVariants: {
    layout: 'vertical',
  },
})

/**
 * Button style variants
 */
const buttonVariants = cva('mt-4 self-start', {
  variants: {
    state: {
      light: 'bg-[#080A0F] text-white',
      dark: 'bg-white text-[#080A0F]',
    },
  },
  defaultVariants: {
    state: 'light',
  },
})

/**
 * Text style variants
 */
const textVariants = cva('', {
  variants: {
    state: {
      light: 'text-[#080A0F]',
      dark: 'text-white',
    },
  },
  defaultVariants: {
    state: 'light',
  },
})

/**
 * Convert ResponsiveBackgroundImage to Picture source string
 * Format: "url1 1920, url2 1439, url3 1024, url4 767, url5"
 */
const getResponsiveSource = (bgImage?: ResponsiveBackgroundImage): string | undefined => {
  if (!bgImage) return undefined

  const sources: string[] = []

  // lg-desktop: ≥1920px
  if (bgImage.lgDesktop?.url) {
    sources.push(`${bgImage.lgDesktop.url} 1920`)
  }
  // desktop: ≥1440px
  if (bgImage.desktop?.url) {
    sources.push(`${bgImage.desktop.url} 1439`)
  }
  // laptop: ≥1025px
  if (bgImage.laptop?.url) {
    sources.push(`${bgImage.laptop.url} 1024`)
  }
  // tablet: ≥768px
  if (bgImage.tablet?.url) {
    sources.push(`${bgImage.tablet.url} 767`)
  }
  // default (mobile): <768px
  if (bgImage.default?.url) {
    sources.push(bgImage.default.url)
  }

  return sources.length > 0 ? sources.join(', ') : undefined
}

/**
 * Get alt text from ResponsiveBackgroundImage
 */
const getBackgroundAlt = (bgImage?: ResponsiveBackgroundImage): string => {
  if (!bgImage) return ''
  return (
    bgImage.default?.alt ||
    bgImage.tablet?.alt ||
    bgImage.laptop?.alt ||
    bgImage.desktop?.alt ||
    bgImage.lgDesktop?.alt ||
    ''
  )
}

/**
 * Format countdown time
 */
const formatTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * 解析开始时间，支持 ISO 8601 字符串或时间戳
 */
const parseStartTime = (startTime: string | number): number => {
  if (typeof startTime === 'number') {
    return startTime
  }
  // 解析 ISO 8601 字符串
  const parsed = new Date(startTime).getTime()
  return isNaN(parsed) ? 0 : parsed
}

/**
 * 计算当前是否处于轮次切换期（下一轮开始前5分钟）
 * @param countdown 倒计时配置
 * @returns 是否处于切换期
 */
const isInTransitionPeriod = (countdown?: CountdownConfig): boolean => {
  if (!countdown) return false
  const startTimeMs = parseStartTime(countdown.startTime)
  const durationMs = (countdown.durationDays ?? 1) * 24 * 3600 * 1000
  const rounds = countdown.rounds ?? 1
  const now = Date.now()

  // 未到开始时间
  if (now < startTimeMs) return false

  // 计算当前轮次
  const elapsed = now - startTimeMs
  const currentRound = Math.floor(elapsed / durationMs) + 1

  // 所有轮次已结束
  if (currentRound > rounds) return false

  // 计算当前轮次的结束时间
  const currentRoundEndTime = startTimeMs + currentRound * durationMs

  // 距离当前轮次结束的时间
  const timeUntilRoundEnd = currentRoundEndTime - now

  // 如果距离结束不到5分钟，处于切换期
  const TRANSITION_PERIOD_MS = 5 * 60 * 1000 // 5分钟
  return timeUntilRoundEnd <= TRANSITION_PERIOD_MS && timeUntilRoundEnd > 0
}

/**
 * 根据倒计时配置计算当前轮次的 Code
 * @param codePrefix Code 前缀
 * @param countdown 倒计时配置
 * @returns 完整的 Code（前缀 + MMDD 后缀）
 * @description 统一使用 UTC 时间计算，确保全球任何时区得到相同的 code
 */
const getCodeWithSuffix = (codePrefix?: string, countdown?: CountdownConfig): string => {
  if (!codePrefix || !countdown) return codePrefix || ''

  const startTimeMs = parseStartTime(countdown.startTime)
  const durationMs = (countdown.durationDays ?? 1) * 24 * 3600 * 1000
  const rounds = countdown.rounds ?? 1
  const now = Date.now()

  // 未到开始时间，使用第一轮的日期（UTC 时间）
  if (now < startTimeMs) {
    const date = new Date(startTimeMs)
    const suffix = `${(date.getUTCMonth() + 1).toString().padStart(2, '0')}${date.getUTCDate().toString().padStart(2, '0')}`
    return `${codePrefix}${suffix}`
  }

  // 计算当前处于第几轮（从 1 开始）
  const elapsed = now - startTimeMs
  const currentRound = Math.min(Math.floor(elapsed / durationMs) + 1, rounds)

  // 计算当前轮次的开始日期
  const currentRoundStartMs = startTimeMs + (currentRound - 1) * durationMs
  const date = new Date(currentRoundStartMs)

  // 格式化为 MMDD（使用 UTC 时间）
  const suffix = `${(date.getUTCMonth() + 1).toString().padStart(2, '0')}${date.getUTCDate().toString().padStart(2, '0')}`

  return `${codePrefix}${suffix}`
}

/**
 * 倒计时状态类型
 */
type CountdownState = 'beforeStart' | 'running' | 'lastRound' | 'finished'

/**
 * Countdown Component
 * 支持多轮倒计时，显示不同状态的标签
 */
const Countdown = React.memo(
  ({
    config,
    theme = 'light',
    className,
    countdownText = 'Next round start time',
    lastRoundText = 'Last round',
    beforeStartText = 'Start time',
    countdownIcon,
    onStateChange,
  }: {
    config: CountdownConfig
    theme?: 'light' | 'dark'
    className?: string
    countdownText?: string
    lastRoundText?: string
    beforeStartText?: string
    countdownIcon?: Media
    onStateChange?: (state: CountdownState) => void
  }) => {
    const [timeLeft, setTimeLeft] = React.useState(0)
    const [countdownState, setCountdownState] = React.useState<CountdownState>('beforeStart')

    // 解析开始时间为时间戳
    const startTimeMs = React.useMemo(() => parseStartTime(config.startTime), [config.startTime])

    const rounds = config.rounds ?? 1
    // 默认 1 天，转换为毫秒 (1天 = 24小时 = 86400秒 = 86400000毫秒)
    const durationMs = (config.durationDays ?? 1) * 24 * 3600 * 1000

    React.useEffect(() => {
      const calculateCountdown = () => {
        const now = Date.now()

        // 未到开始时间，显示开始前倒计时
        if (now < startTimeMs) {
          const diff = Math.max(0, Math.floor((startTimeMs - now) / 1000))
          setTimeLeft(diff)
          setCountdownState('beforeStart')
          return
        }

        // 计算已经过去的时间
        const elapsed = now - startTimeMs
        // 计算当前处于第几轮（从 1 开始）
        const currentRound = Math.floor(elapsed / durationMs) + 1

        // 所有轮次已结束
        if (currentRound > rounds) {
          setCountdownState('finished')
          return
        }

        // 计算当前轮次的目标时间
        const currentRoundTargetTime = startTimeMs + currentRound * durationMs
        // 计算剩余秒数
        const diff = Math.max(0, Math.floor((currentRoundTargetTime - now) / 1000))

        setTimeLeft(diff)
        // 判断是否是最后一轮
        setCountdownState(currentRound === rounds ? 'lastRound' : 'running')
      }

      calculateCountdown()
      const timer = setInterval(calculateCountdown, 1000)

      return () => clearInterval(timer)
    }, [startTimeMs, durationMs, rounds])

    // 通知父组件状态变化
    React.useEffect(() => {
      onStateChange?.(countdownState)
    }, [countdownState, onStateChange])

    // 根据状态获取显示的标签文本
    const labelText = React.useMemo(() => {
      switch (countdownState) {
        case 'beforeStart':
          return beforeStartText
        case 'lastRound':
          return lastRoundText
        default:
          return countdownText
      }
    }, [countdownState, beforeStartText, lastRoundText, countdownText])

    // 所有轮次已结束，不渲染
    if (countdownState === 'finished') {
      return null
    }

    return (
      <div
        className={cn(
          'desktop:gap-2 laptop:text-base flex items-center gap-1 text-sm',
          theme === 'dark' ? 'text-white' : 'text-[#080A0F]',
          className
        )}
      >
        {countdownIcon && (
          <Picture
            source={countdownIcon.url}
            alt={countdownIcon.alt}
            className="laptop:size-5 size-4 shrink-0"
            imgClassName="size-full object-contain"
          />
        )}
        <Text html={labelText} className="whitespace-nowrap font-bold" />
        <span className="font-bold">|</span>
        <Text className="font-bold" html={formatTime(timeLeft)} />
      </div>
    )
  }
)

Countdown.displayName = 'Countdown'

/**
 * Progress Bar Component
 */
const ProgressBar = React.memo(
  ({ progress, theme = 'light', className }: { progress: number; theme?: 'light' | 'dark'; className?: string }) => {
    // Clamp progress between 0 and 100
    const clampedProgress = Math.min(100, Math.max(0, progress))

    return (
      <div
        className={cn(
          'flex h-2 w-full items-stretch overflow-hidden rounded-full',
          theme === 'dark' ? 'bg-white/20' : 'bg-black/20',
          className
        )}
        role="progressbar"
        aria-valuenow={clampedProgress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300',
            theme === 'dark' ? 'bg-white' : 'bg-[#080A0F]'
          )}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    )
  }
)

ProgressBar.displayName = 'ProgressBar'

/**
 * Gift Shelf Card Component
 */
const GiftShelfCard = React.memo(
  ({
    item,
    theme = 'light',
    layout = 'vertical',
    className,
    buttonClassName,
    countdownClassName,
    onButtonClick,
    buildData,
    buttonText,
    remainText,
    lowStockText,
    soldOutButtonText,
    discountText = 'Only {price}',
    countdownText,
    countdownIcon,
    lastRoundText,
    beforeStartText,
    comingSoonButtonText,
    loading,
  }: {
    item: GiftShelfItem
    theme?: 'light' | 'dark'
    layout?: 'vertical' | 'horizontal'
    className?: string
    buttonClassName?: string
    countdownClassName?: string
    onButtonClick?: ({ product, code }: { product: GiftShelfProduct; code: string }) => void
    buildData?: {
      products: Product[]
    }
    buttonText: string
    remainText?: string
    lowStockText?: string
    soldOutButtonText?: string
    discountText?: string
    countdownText?: string
    countdownIcon?: Media
    lastRoundText?: string
    beforeStartText?: string
    comingSoonButtonText?: string
    loading?: boolean
  }) => {
    const { locale = 'us' } = useAiuiContext()
    // 获取第一个产品的简化数据
    const firstProduct = item.products?.[0]

    // 检测是否处于轮次切换期 + 当前轮次 code（每秒更新）
    const [isTransitioning, setIsTransitioning] = React.useState(() => isInTransitionPeriod(item.countdown))
    const [currentCode, setCurrentCode] = React.useState(() => getCodeWithSuffix(item.codePrefix, item.countdown))
    // 倒计时状态
    const [countdownState, setCountdownState] = React.useState<CountdownState>('beforeStart')

    React.useEffect(() => {
      const checkAndUpdate = () => {
        // 更新轮次切换状态
        setIsTransitioning(isInTransitionPeriod(item.countdown))
        // 更新当前轮次 code（仅在值变化时更新）
        const newCode = getCodeWithSuffix(item.codePrefix, item.countdown)
        setCurrentCode(prev => (prev !== newCode ? newCode : prev))
      }

      // 初始检查
      checkAndUpdate()
      // 每秒检查一次
      const timer = setInterval(checkAndUpdate, 1000)

      return () => clearInterval(timer)
    }, [item.codePrefix, item.countdown])

    // 倒计时状态变化回调
    const handleCountdownStateChange = React.useCallback((state: CountdownState) => {
      setCountdownState(state)
    }, [])

    // 通过 handle 匹配 buildData 中的完整产品数据
    const fullProduct = React.useMemo(() => {
      if (!firstProduct?.handle || !buildData?.products?.length) return null
      return buildData.products.find(p => p.handle === firstProduct.handle) || null
    }, [firstProduct?.handle, buildData?.products])

    const fullProductVariant = React.useMemo(
      () => fullProduct?.variants?.find(variant => variant?.sku === firstProduct?.sku) || ({} as ProductVariant),
      [firstProduct?.sku, fullProduct?.variants]
    )

    // 组合展示数据（优先使用详细数据，回退到简化数据）
    const displayData = React.useMemo(() => {
      const { price, basePrice } = formatVariantPrice({
        locale: locale || 'us',
        baseAmount: fullProductVariant?.price?.amount,
        amount: firstProduct?.custom_price ?? fullProductVariant?.price?.amount,
        currencyCode: fullProduct?.price?.currencyCode || 'USD',
      })

      return {
        // 主价格/价值展示：有详细数据时显示价格，否则显示产品名
        value: basePrice,
        // 产品描述/标题：有详细数据时显示标题，否则为空（避免重复显示）
        description: firstProduct?.custom_description,
        // 优惠价格
        salePrice: replaceTemplate(discountText || '', {
          price,
        }),
      }
    }, [
      fullProduct,
      fullProductVariant,
      locale,
      discountText,
      firstProduct?.custom_price,
      firstProduct?.custom_description,
    ])

    const handleClick = React.useCallback(() => {
      onButtonClick?.({ product: firstProduct, code: currentCode })
    }, [onButtonClick, currentCode, firstProduct])

    return (
      <div className={cn(giftCardVariants({ state: theme }), className)} data-ui-component-id="GiftShelfCard">
        {/* Countdown section - above the card content (only in vertical layout) */}
        {item.countdown && (
          <div className="laptop:h-[24px] mb-4 h-[20px]">
            <Countdown
              config={item.countdown}
              theme={theme}
              className={countdownClassName}
              countdownText={countdownText}
              lastRoundText={lastRoundText}
              beforeStartText={beforeStartText}
              countdownIcon={countdownIcon}
              onStateChange={handleCountdownStateChange}
            />
          </div>
        )}

        {/* Content layout wrapper - 控制 Background image 和 Bottom info area 的布局 */}
        <div className={contentLayoutVariants({ layout })}>
          {/* Background image - responsive across 5 breakpoints */}
          <div className={imageAreaVariants({ layout })}>
            {item.backgroundImage && (
              <Picture
                source={getResponsiveSource(item.backgroundImage)}
                alt={getBackgroundAlt(item.backgroundImage)}
                className="rounded-card size-full overflow-hidden object-cover"
                imgClassName="h-full w-full object-cover"
              />
            )}
            {/* Card content area */}
            <div className="absolute top-1/2 z-10 w-full -translate-y-1/2 px-6">
              {/* Value and description */}
              <div className="flex flex-col gap-1">
                {/* Main value display */}
                {displayData.value && (
                  <Heading
                    html={displayData.value}
                    size={4}
                    className={cn('font-bold leading-none', textVariants({ state: theme }))}
                  />
                )}

                {/* Description */}
                {displayData.description && (
                  <Text
                    html={displayData.description}
                    className={cn('text-base font-bold opacity-60', textVariants({ state: theme }))}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Bottom/Right info area */}
          <div className={infoAreaVariants({ layout })}>
            {/* Sale price */}
            {displayData.salePrice && (
              <Text
                as="p"
                html={displayData.salePrice}
                className={cn('lg-desktop:text-2xl text-xl font-bold', textVariants({ state: theme }))}
              />
            )}

            {/* Progress bar - 超卖时隐藏（availableForSale=true 且 quantityAvailable<=0） */}
            <ProgressBar
              progress={
                firstProduct?.custom_inventory
                  ? ((fullProductVariant?.quantityAvailable || 0) / firstProduct.custom_inventory) * 100
                  : 0
              }
              theme={theme}
              className={cn(
                'mb-1 mt-2',
                fullProductVariant?.availableForSale && (fullProductVariant?.quantityAvailable ?? 0) <= 0 && 'invisible'
              )}
            />

            {/* Remain text - 正常库存提示 */}
            {remainText && (fullProductVariant?.quantityAvailable ?? 0) >= 0 && (
              <Text
                html={replaceTemplate(remainText, {
                  inventory: firstProduct?.custom_inventory?.toString() || '',
                  quantity: fullProductVariant?.quantityAvailable?.toString() || '0',
                })}
                as="p"
                className={cn('laptop:text-base text-sm font-bold', textVariants({ state: theme }))}
              />
            )}

            {/* Low stock text - 支持超卖时显示（availableForSale=true 但 quantityAvailable<=0） */}
            {lowStockText &&
              fullProductVariant?.availableForSale &&
              (fullProductVariant?.quantityAvailable ?? 0) < 0 && (
                <Text
                  html={lowStockText}
                  as="p"
                  className={cn('laptop:text-base text-sm font-bold text-red-500', textVariants({ state: theme }))}
                />
              )}

            {/* Action button */}
            <Button
              size="lg"
              className={cn(buttonVariants({ state: theme }), buttonClassName)}
              onClick={handleClick}
              disabled={
                !fullProductVariant?.availableForSale ||
                loading ||
                isTransitioning ||
                countdownState === 'beforeStart' ||
                countdownState === 'finished'
              }
              loading={loading && fullProductVariant?.availableForSale}
            >
              {countdownState === 'beforeStart'
                ? comingSoonButtonText || buttonText
                : fullProductVariant?.availableForSale
                  ? buttonText
                  : soldOutButtonText || buttonText}
            </Button>
          </div>
        </div>
      </div>
    )
  }
)

GiftShelfCard.displayName = 'GiftShelfCard'

/**
 * GiftShelf - Gift/Promotion Shelf Component
 *
 * @description Display gift cards with countdown, pricing, and purchase buttons
 */
const GiftShelf = React.forwardRef<HTMLDivElement, GiftShelfProps>(
  ({ classNames = {}, data, onButtonClick, buildData, loading, ...rest }, ref) => {
    const theme = data.theme || 'light'

    const itemsPerRow = React.useMemo(() => {
      return data?.items?.length || 4
    }, [data?.items])

    // 当卡片数量为 2 时使用水平布局，否则使用垂直布局
    const cardLayout = itemsPerRow === 2 ? 'horizontal' : 'vertical'

    const swiperBreakpoints = React.useMemo(() => {
      const getBreakpointConfig = (breakpoint: 'mobile' | 'tablet' | 'laptop' | 'desktop') => {
        switch (breakpoint) {
          case 'mobile':
            return { slidesPerView: 1.2, spaceBetween: 12 }
          case 'tablet':
            if (itemsPerRow <= 2) return { slidesPerView: 2, spaceBetween: 12 }
            return { slidesPerView: 2.5, spaceBetween: 12 }
          case 'laptop':
            if (itemsPerRow <= 3) return { slidesPerView: itemsPerRow, spaceBetween: 16 }
            return { slidesPerView: 3.01, spaceBetween: 16 }
          case 'desktop':
            return { slidesPerView: Math.min(itemsPerRow, 4), spaceBetween: 20 }
        }
      }

      return {
        0: getBreakpointConfig('mobile'),
        768: getBreakpointConfig('tablet'),
        1025: getBreakpointConfig('laptop'),
        1440: getBreakpointConfig('desktop'),
      }
    }, [itemsPerRow])

    return (
      <Container
        ref={ref}
        className={cn(classNames?.root)}
        childClassName="overflow-hidden"
        data-ui-component-id="GiftShelf"
        {...rest}
      >
        {/* Cards swiper */}
        <Swiper breakpoints={swiperBreakpoints} className="w-full !overflow-visible">
          {data.items.map((item, index) => (
            <SwiperSlide key={'giftShelfCardItem' + index}>
              <GiftShelfCard
                item={item}
                theme={theme}
                layout={cardLayout}
                className={classNames?.card}
                buttonClassName={classNames?.button}
                countdownClassName={classNames?.countdown}
                onButtonClick={onButtonClick}
                buildData={buildData}
                buttonText={data.buttonText}
                discountText={data.discountText}
                remainText={data.remainText}
                lowStockText={data.lowStockText}
                soldOutButtonText={data.soldOutButtonText}
                countdownText={data.countdownText}
                lastRoundText={data.lastRoundText}
                countdownIcon={data?.countdownIcon}
                beforeStartText={data.beforeStartText}
                comingSoonButtonText={data.comingSoonButtonText}
                loading={loading}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Container>
    )
  }
)

GiftShelf.displayName = 'GiftShelf'

export default withLayout(GiftShelf)
export type {
  GiftShelfProps,
  GiftShelfData,
  GiftShelfItem,
  GiftShelfProduct,
  CountdownConfig,
  ResponsiveBackgroundImage,
} from './types.js'
